/**
 * Copyright (C) Radiojar S.A. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 *
 * Created by Maria Konstantinidou on 20/5/25.
 */

import emitter from "@/emitter";
import StationApi from "@/services/Station";
import config from "@/config";

export default {
	state: {
		studioSettings: null,

		activeStudioInputDevices: [],

		activeStudioPlayoutOutputDevice: null,

		activeStudioAuditionOutputDevice: null,

		activeStudioHotkeysOutputDevices: []
	},

	getters: {
		getStudioSettings: state => () => {
			return state.studioSettings;
		},

		isWebRTCStudio: state => () => {
			return state.studioSettings?.sip_connection_address?.includes("webRTC");
		},

		getActiveStudioInputDevices: state => () => {
			return state.activeStudioInputDevices;
		},

		getActiveStudioPlayoutOutputDevice: state => () => {
			return state.activeStudioPlayoutOutputDevice;
		},

		getActiveStudioAuditionOutputDevice: state => () => {
			return state.activeStudioAuditionOutputDevice;
		},

		getActiveStudioHotkeysOutputDevices: state => () => {
			return state.activeStudioHotkeysOutputDevices;
		}
	},

	mutations: {
		setStudioSettings(state, config) {
			// Migrate legacy midi.ihm → midi.default
			// TODO: remove this after migration period (released on 2.29.0)
			if (config?.preferences?.midi?.ihm) {
				config.preferences.midi.default = config.preferences.midi.ihm;
				delete config.preferences.midi.ihm;
			}
			state.studioSettings = config;
		},

		setActiveStudioInputDevices(state, devices) {
			state.activeStudioInputDevices = devices;
		},

		setActiveStudioPlayoutOutputDevice(state, devices) {
			state.activeStudioPlayoutOutputDevice = devices;
		},

		setActiveStudioAuditionOutputDevice(state, devices) {
			state.activeStudioAuditionOutputDevice = devices;
		},

		setActiveStudioHotkeysOutputDevices(state, devices) {
			state.activeStudioHotkeysOutputDevices = devices;
		}
	},

	actions: {
		getStudioSettings({getters, commit, dispatch}) {
			window.history.replaceState(window.location.href, "", window.location.href.replace(/[\\/]$/, ''));
			const studioId = new URLSearchParams(location.search).get("studio");

			if (studioId) {
				return dispatch(
					"getUserFeatures",
					{object_id: getters.getOrgID(), service_id: config.PORTAL_SERVICE_ID}
				).then(() => {
					const manageStudioFeatureEnabled = getters.isFeatureEnabled({
						object_id: getters.getOrgID(),
						service_id: config.PORTAL_SERVICE_ID,
						reference: "portal_service_manage_studio_settings"
					});

					const localSettings = !manageStudioFeatureEnabled && getters.userPreferences("studio_settings") ||
						{};

					return StationApi.getStudioSettings(studioId)
									 .then(response => {
										 const settings = {...response.data, ...localSettings};

										 const isPrivate = !!settings?.user_id;
										 if (isPrivate && settings.user_id !== getters.getUserID()) {
											 commit("SHOW_NOTIFICATION_WITH_TYPE", {
												 message: "Selected studio is private and belongs to another user."
											 });
											 history.replaceState(null, "", location.href.split("?")[0]);
											 return null;
										 }
										 else {
											 commit("setStudioSettings", settings);
											 dispatch("setActiveStudioDevices");
											 if (getters.liveMode()) {
												 dispatch("getAstrumPlayerLiveState", settings);	//Check if the studio is already connected to any station
											 }
											 return settings;
										 }

									 })
									 .catch(error => {
										 commit("setStudioSettings", null);
										 if (error?.response?.status === 404) {
											 if (manageStudioFeatureEnabled) {
												 emitter.emit("add-new-studio-settings");
											 }
										 }
										 else {
											 commit(
												 "SHOW_NOTIFICATION_WITH_TYPE",
												 {message: "An error occurred when trying to get the Studio Settings."}
											 );
										 }
									 });
				});
			}
		},

		searchStudioSettings(store, params = {search: ""}) {
			return StationApi.searchStudioSettings(params)
							 .then(response => response.data.results)
							 .catch(() => []);
		},

		setActiveStudioDevices({getters, commit}) {
			const studioSettings = getters.getStudioSettings();
			if (studioSettings) {
				const activeStudioInputDevices = [];
				studioSettings.pc_audio_in.forEach(d => {
					// We need to keep the system audio devices that apply the saved studio input devices
					const device = getters.systemInputDevices()?.find(({name}) => name === d.name);
					if (device) {
						activeStudioInputDevices.push(device);
					}
				});

				const activeStudioPlayoutOutputDevice = getters.systemOutputDevices()?.find(
					d => d.name === studioSettings.pc_audio_out_playout[0]?.name
				);

				const activeStudioAuditionOutputDevice = getters.systemOutputDevices()?.find(
					d => d.name === studioSettings.pc_audio_out_audition?.name
				);

				const activeStudioHotkeysOutputDevices = [];
				studioSettings.pc_audio_out.forEach(d => {
					// We need to keep the system audio devices that apply the saved studio output devices
					const device = getters.systemOutputDevices()?.find(({name}) => name === d.name);
					if (device) {
						activeStudioHotkeysOutputDevices.push(device);
					}
				});

				commit("setActiveStudioInputDevices", activeStudioInputDevices);
				commit("setActiveStudioPlayoutOutputDevice", activeStudioPlayoutOutputDevice);
				commit("setActiveStudioAuditionOutputDevice", activeStudioAuditionOutputDevice);
				commit("setActiveStudioHotkeysOutputDevices", activeStudioHotkeysOutputDevices);
			}
		},

		createStudioSettings({commit}, payload) {
			return StationApi.postStudioSettings(payload).then(response => {
				commit("setStudioSettings", response.data);
				return response.data;
			});
		},

		editStudioSettings({commit}, payload) {
			return StationApi.putStudioSettings(payload).then(response => {
				commit("setStudioSettings", response.data);
				return response.data;
			});
		}
	}
};